// Global React Error Boundary — catches rendering exceptions anywhere in the
// component tree and renders a graceful parchment-themed fallback screen
// instead of a white screen of death. Users can reload the page, return home
// or clear local state if they're stuck in a broken cached session.
//
// Must be a class component: `getDerivedStateFromError` and `componentDidCatch`
// are class-only React lifecycle hooks.
import React from "react";

function isItalian() {
  try {
    const stored = localStorage.getItem("ac_lang");
    if (stored) return stored.toLowerCase().startsWith("it");
    return (navigator.language || "en").toLowerCase().startsWith("it");
  } catch {
    return false;
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console for devs + optional remote logging hook
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    this.setState({ info });
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch { /* noop */ }
  };

  handleHome = () => {
    try {
      window.location.href = "/";
    } catch { /* noop */ }
  };

  handleHardReset = () => {
    try {
      // Preserve the legal/ui lang preference so the fallback stays translated
      const lang = localStorage.getItem("ac_lang");
      localStorage.clear();
      if (lang) localStorage.setItem("ac_lang", lang);
    } catch { /* noop */ }
    this.handleReload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isIt = isItalian();
    const title = isIt ? "La pergamena si è strappata" : "The scroll has torn";
    const body = isIt
      ? "Un errore inaspettato ha interrotto la tua avventura. Non temere: la tua storia è al sicuro. Ricarica la pagina per riprenderla."
      : "An unexpected error interrupted your adventure. Fear not: your story is safe. Reload the page to resume it.";
    const btnReload = isIt ? "Ricarica" : "Reload";
    const btnHome = isIt ? "Torna alla home" : "Back to home";
    const btnReset = isIt ? "Reset sessione locale" : "Reset local session";
    const dev = process.env.NODE_ENV === "development";
    const errMsg = this.state.error?.message || String(this.state.error || "");

    return (
      <div
        className="min-h-screen bg-[#0a0a0c] text-[#e2e8f0] flex items-center justify-center px-4 py-10"
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full bg-[#1a1a1d] border-2 border-[#cfb53b]/40 rounded-xl p-6 sm:p-8 shadow-[0_12px_60px_rgba(0,0,0,0.7)]">
          {/* Decorative torn scroll icon */}
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#cfb53b]/10 border border-[#cfb53b]/40 flex items-center justify-center">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#cfb53b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 3h12a3 3 0 0 1 3 3v12a3 3 0 0 0 3 3" />
              <path d="M4 3v15a3 3 0 0 0 3 3h8" />
              <path d="M10 12l4 4M14 12l-4 4" />
            </svg>
          </div>

          <h1 className="font-heading text-xl sm:text-2xl text-[#cfb53b] text-center mb-3" data-testid="error-boundary-title">
            {title}
          </h1>
          <p className="text-sm text-[#cbd5e1] text-center leading-relaxed mb-5">
            {body}
          </p>

          <div className="flex flex-col gap-2">
            <button
              onClick={this.handleReload}
              className="w-full bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading py-2.5 rounded-md transition-colors"
              data-testid="error-boundary-reload"
            >
              {btnReload}
            </button>
            <button
              onClick={this.handleHome}
              className="w-full bg-transparent border border-[#cfb53b]/40 text-[#cfb53b] hover:bg-[#cfb53b]/10 py-2 rounded-md transition-colors text-sm"
              data-testid="error-boundary-home"
            >
              {btnHome}
            </button>
            <button
              onClick={this.handleHardReset}
              className="w-full text-[#94a3b8] hover:text-[#cfb53b] py-1.5 text-xs transition-colors"
              data-testid="error-boundary-reset"
            >
              {btnReset}
            </button>
          </div>

          {dev && errMsg && (
            <details className="mt-5 bg-[#0a0a0c] border border-[#2d3748] rounded p-2">
              <summary className="text-xs text-[#cfb53b]/70 cursor-pointer font-heading">
                {isIt ? "Dettagli tecnici (solo dev)" : "Technical details (dev only)"}
              </summary>
              <pre className="text-[10px] text-[#94a3b8] mt-2 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                {errMsg}
                {this.state.info?.componentStack || ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
